import React, { useState, useCallback, useEffect } from 'react';
import { Box, Container, Grid, Alert, Snackbar, CircularProgress, Typography, useMediaQuery, useTheme } from '@mui/material';
import MapboxMap from './MapboxMap';
import ParameterPanel from './ParameterPanel';
import ShadowProjection from './ShadowProjection';
import SavedLocationsPanel from './SavedLocationsPanel';
import SaveLocationDialog from './SaveLocationDialog';
import Header from './Header';
import { VineyardParameters, Location, ShadowInfo, SavedLocation, SavedLocationCreate } from '../types';
import { calculateShadow, getOptimalTimeWindow, createSession, clearTimezoneCache, calculateOptimalWindows } from '../services/api';
import { getSavedLocations, getSavedLocation, createSavedLocation, updateSavedLocation, deleteSavedLocation, reorderSavedLocations } from '../services/savedLocations';
import useDebounce from '../hooks/useDebounce';
import { useAuth } from '../contexts/AuthContext';

const DEFAULT_LOCATION: Location = {
  latitude: 47.6062,
  longitude: -122.3321
};

const DEFAULT_PARAMETERS: VineyardParameters = {
  row_direction: 0,
  plant_height: 100,
  row_distance: 100,
  min_angle_from_row: 30,
  min_distance_from_row: 30
};

// Add this helper function at the top level
const formatTimeString = (date: Date): string => {
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
};

const MainApp: React.FC = () => {
  const { user } = useAuth();
  
  // Theme and responsive state
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Session state
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isCreatingSession, setIsCreatingSession] = useState<boolean>(false);
  
  // Location state
  const [location, setLocation] = useState<Location>(DEFAULT_LOCATION);
  
  // Parameters state
  const [parameters, setParameters] = useState<VineyardParameters>(DEFAULT_PARAMETERS);
  
  // Shadow calculation results
  const [shadowInfo, setShadowInfo] = useState<ShadowInfo | null>(null);
  const [optimalWindows, setOptimalWindows] = useState<any[]>([]);
  
  // Date and time state
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [currentTime, setCurrentTime] = useState<Date>(() => {
    const now = new Date();
    const minutes = Math.round(now.getMinutes() / 15) * 15;
    const hours = now.getHours();
    const roundedNow = new Date();
    roundedNow.setHours(hours, minutes, 0, 0);
    return roundedNow;
  });

  // Saved locations state
  const [savedLocations, setSavedLocations] = useState<SavedLocation[]>([]);
  const [selectedSavedLocationId, setSelectedSavedLocationId] = useState<string | null>(null);
  const [saveDialogOpen, setSaveDialogOpen] = useState<boolean>(false);
  const [defaultLocationName, setDefaultLocationName] = useState<string>('');
  const [locationName, setLocationName] = useState<string>('');
  const [isLoadingSavedLocations, setIsLoadingSavedLocations] = useState<boolean>(false);
  
  // Error handling
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Loading states
  const [isLoadingShadows, setIsLoadingShadows] = useState(false);
  const [isLoadingOptimal, setIsLoadingOptimal] = useState(false);

  // Add a state variable to trigger status updates only when sliders are released
  const [statusUpdateTrigger, setStatusUpdateTrigger] = useState(0);

  // Track whether this is an initial load or update
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  
  // Track the last update type (visual only or full status update)
  const [lastUpdateType, setLastUpdateType] = useState<'visual' | 'full'>('visual');
  
  // Track if any slider is currently being moved
  const [isSliderMoving, setIsSliderMoving] = useState(false);

  // Route visibility state
  const [isRouteVisible, setIsRouteVisible] = useState<boolean>(false);
  const [routeGeometry, setRouteGeometry] = useState<{
    betweenLocations: any | null;
    fromCurrentLocation: any | null;
  }>({
    betweenLocations: null,
    fromCurrentLocation: null
  });

  // Create a session when component mounts
  useEffect(() => {
    const initSession = async () => {
      setIsCreatingSession(true);
      try {
        // Create session first
        const result = await createSession({
          name: 'Vineyard Session'
        });
        
        setSessionId(result.id);
        setLastUpdateType('full');
        setIsInitialLoad(false);  // Mark that initial load is complete
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to create session');
        console.error('Session creation error:', err);
      } finally {
        setIsCreatingSession(false);
      }
    };

    initSession();
  }, []);  // Keep empty dependency array since this should only run once

  // Fetch saved locations only when authenticated
  useEffect(() => {
    if (!user) return;  // Skip if not authenticated

    const fetchSavedLocations = async () => {
      try {
        setIsLoadingSavedLocations(true);
        const locations = await getSavedLocations();
        setSavedLocations(locations);
      } catch (err) {
        console.error('Error fetching saved locations:', err);
        setError('Failed to load saved locations. Please try again later.');
      } finally {
        setIsLoadingSavedLocations(false);
      }
    };

    fetchSavedLocations();
  }, [user]);  // Only run when user auth state changes

  // Calculate shadows
  useEffect(() => {
    if (!sessionId) return;
    
    const isVisualOnlyUpdate = lastUpdateType === 'visual' && !isInitialLoad;
    
    const fetchShadowInfo = async () => {
      if (!isVisualOnlyUpdate) {
        setIsLoadingShadows(true);
      }
      
      try {
        const dateString = selectedDate.toISOString().split('T')[0];
        const timeString = formatTimeString(currentTime);
        
        const normalizedRowDirection = ((parameters.row_direction % 360) + 360) % 360;
        
        const result = await calculateShadow(sessionId, {
          ...location,
          ...parameters,
          row_direction: normalizedRowDirection,
          date: dateString,
          time: timeString
        });
        
        setShadowInfo(result);
      } catch (err) {
        console.error("Shadow calculation error:", err);
        if (!isVisualOnlyUpdate) {
          setError(err instanceof Error ? err.message : 'Failed to calculate shadows');
        }
      } finally {
        if (!isVisualOnlyUpdate) {
          setIsLoadingShadows(false);
        }
      }
    };
    
    fetchShadowInfo();
  }, [sessionId, location, selectedDate, currentTime, parameters, lastUpdateType, isInitialLoad]);

  // Calculate optimal time windows
  useEffect(() => {
    if (!sessionId) return;
    
    // Skip calculation during visual updates (time slider movement)
    // But allow it during initial load
    if (!isInitialLoad && lastUpdateType === 'visual') return;
    
    setIsLoadingOptimal(true);
    
    const fetchOptimalWindows = async () => {
      try {
        const dateString = selectedDate.toISOString().split('T')[0];
        const normalizedRowDirection = ((parameters.row_direction % 360) + 360) % 360;
        
        const result = await calculateOptimalWindows(sessionId, {
          ...location,
          ...parameters,
          row_direction: normalizedRowDirection,
          date: dateString
        });
        
        // Extract optimal_windows array from the response
        if (result && result.optimal_windows && Array.isArray(result.optimal_windows)) {
          setOptimalWindows(result.optimal_windows);
        } else {
          console.error("Invalid optimal windows response format:", result);
          setOptimalWindows([]);
        }
      } catch (err) {
        console.error("Optimal time window error:", err);
        setError(err instanceof Error ? err.message : 'Failed to fetch optimal time windows');
        setOptimalWindows([]);
      } finally {
        setIsLoadingOptimal(false);
      }
    };

    fetchOptimalWindows();
  }, [
    sessionId,
    location.latitude,
    location.longitude,
    selectedDate,
    parameters.row_direction,
    parameters.plant_height,
    parameters.row_distance,
    parameters.min_angle_from_row,
    parameters.min_distance_from_row,
    isInitialLoad,
    lastUpdateType // Keep this to properly handle initial load case
  ]);

  // All the handlers from App.tsx
  const handleLocationChange = useCallback((newLat: number, newLng: number) => {
    setLocation({
      latitude: newLat,
      longitude: newLng
    });
  }, []);

  const handleParameterChange = useCallback((name: keyof VineyardParameters, value: number, visualUpdateOnly: boolean = false) => {
    if (name === 'row_direction') {
      value = value % 360;
      if (value > 180) value = value - 360;
      else if (value < -180) value = value + 360;
    }
    
    setParameters(prev => ({ ...prev, [name]: value }));
    setLastUpdateType(visualUpdateOnly ? 'visual' : 'full');
    setIsSliderMoving(visualUpdateOnly);
    
    if (!visualUpdateOnly) {
      setStatusUpdateTrigger(prev => prev + 1);
    }
  }, []);

  const handleDateChange = useCallback((date: Date | null) => {
    if (date) {
      const newDate = new Date(date);
      newDate.setHours(currentTime.getHours(), currentTime.getMinutes(), 0, 0);
      setSelectedDate(newDate);
    }
  }, [currentTime]);

  const handleTimeChange = useCallback((time: Date, isMoving: boolean = false) => {
    // Ensure time has zero seconds and milliseconds
    const newTime = new Date(time);
    newTime.setSeconds(0, 0);
    setCurrentTime(newTime);
    setIsSliderMoving(isMoving);
    
    // Keep lastUpdateType as 'visual' for time changes
    setLastUpdateType('visual');
    
    // Still update the status trigger for shadow calculations
    if (!isMoving) {
      setStatusUpdateTrigger(prev => prev + 1);
    }
  }, []);

  const handleNextDay = useCallback(() => {
    setSelectedDate(prev => {
      const next = new Date(prev);
      next.setDate(next.getDate() + 7);
      return next;
    });
  }, []);

  const handlePreviousDay = useCallback(() => {
    setSelectedDate(prev => {
      const prev_day = new Date(prev);
      prev_day.setDate(prev_day.getDate() - 7);
      return prev_day;
    });
  }, []);

  const handleSelectLocation = useCallback(async (savedLocation: SavedLocation) => {
    try {
      if (savedLocation.id === "-1") {
        setParameters(DEFAULT_PARAMETERS);
        setSelectedSavedLocationId(null);
        setLocationName('');
        setLastUpdateType('full');  // Force recalculation for new location
        setSuccessMessage('Ready to create a new location');
        return;
      }
      
      const fullLocation = await getSavedLocation(savedLocation.id);
      
      setLocation({
        latitude: fullLocation.latitude,
        longitude: fullLocation.longitude
      });
      
      setParameters({
        row_direction: fullLocation.row_direction,
        plant_height: fullLocation.plant_height,
        row_distance: fullLocation.row_distance,
        min_angle_from_row: fullLocation.min_angle_from_row,
        min_distance_from_row: fullLocation.min_distance_from_row
      });
      
      setSelectedDate(new Date(fullLocation.selected_date));
      
      if (fullLocation.time) {
        const [hours, minutes] = fullLocation.time.split(':').map(Number);
        const newTime = new Date(currentTime);
        newTime.setHours(hours, minutes, 0, 0);
        setCurrentTime(newTime);
      }
      
      setLocationName(fullLocation.name);
      setSelectedSavedLocationId(fullLocation.id);
      setLastUpdateType('full');  // Force recalculation for loaded location
      setSuccessMessage(`Loaded "${fullLocation.name}" successfully`);
    } catch (error) {
      console.error('Error loading saved location:', error);
      setError('Failed to load saved location. Please try again.');
    }
  }, [currentTime]);

  const handleLocationNameChange = useCallback((name: string) => {
    setLocationName(name);
  }, []);

  const handleSaveClick = useCallback(() => {
    // If updating an existing location, use the current name and open dialog directly
    if (selectedSavedLocationId !== null) {
      // Use the current location name instead of getting a new one
      setDefaultLocationName(locationName);
      setSaveDialogOpen(true);
      return;
    }

    // For new locations, get a name suggestion from the API
    fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${location.latitude}&lon=${location.longitude}&zoom=14&addressdetails=1`, {
      headers: {
        'User-Agent': 'VineyardShadowTool/1.0'
      }
    })
      .then(response => response.json())
      .then(data => {
        let placeName = '';
        
        if (data.name) {
          placeName = data.name;
        } else if (data.address) {
          for (const key of ['hamlet', 'village', 'town', 'city', 'county']) {
            if (data.address[key]) {
              placeName = data.address[key];
              break;
            }
          }
        }
        
        if (!placeName) {
          placeName = `Location ${location.latitude.toFixed(4)}, ${location.longitude.toFixed(4)}`;
        }
        
        setDefaultLocationName(placeName);
        setSaveDialogOpen(true);
      })
      .catch(error => {
        console.error('Error fetching place name:', error);
        setDefaultLocationName(`Location ${location.latitude.toFixed(4)}, ${location.longitude.toFixed(4)}`);
        setSaveDialogOpen(true);
      });
  }, [location, selectedSavedLocationId, locationName]);

  const handleSaveLocation = useCallback(async (savedLocation: SavedLocation) => {
    // Update the saved locations list with the new/updated location
    setSavedLocations(prevLocations => {
      const index = prevLocations.findIndex(loc => loc.id === savedLocation.id);
      if (index >= 0) {
        const newLocations = [...prevLocations];
        newLocations[index] = savedLocation;
        return newLocations;
      } else {
        return [...prevLocations, savedLocation];
      }
    });
    
    // Update selected location ID and show success message
    setSelectedSavedLocationId(savedLocation.id);
    setSuccessMessage(`${selectedSavedLocationId ? 'Updated' : 'Saved'} "${savedLocation.name}" successfully`);
    
    // Force route recalculation by toggling route visibility
    // This ensures routes are recalculated when a new location is added
    // Toggle off and back on to ensure recalculation
    setIsRouteVisible(false);
    // Small delay to ensure state is updated
    setTimeout(() => {
      setIsRouteVisible(true);
    }, 300);
  }, [selectedSavedLocationId]);

  const handleDeleteLocation = useCallback(async (id: string) => {
    try {
      await deleteSavedLocation(id);
      setSavedLocations(prevLocations => prevLocations.filter(loc => loc.id !== id));
      if (selectedSavedLocationId === id) {
        setSelectedSavedLocationId(null);
      }
      setSuccessMessage('Location deleted successfully');
    } catch (error) {
      console.error('Error deleting location:', error);
      setError('Failed to delete location. Please try again.');
    }
  }, [selectedSavedLocationId]);

  const handleImportLocations = useCallback((importedLocations: SavedLocation[]) => {
    setSavedLocations(prevLocations => [...prevLocations, ...importedLocations]);
    setSuccessMessage(`Imported ${importedLocations.length} locations successfully`);
  }, []);

  const handleReorderLocations = useCallback(async (reorderedLocations: SavedLocation[]) => {
    try {
      setSavedLocations(prevLocations => {
        const locationMap = new Map<string, SavedLocation>();
        prevLocations.forEach(loc => locationMap.set(loc.id, loc));
        reorderedLocations.forEach((loc, index) => {
          if (locationMap.has(loc.id)) {
            locationMap.set(loc.id, { ...loc, order: index });
          }
        });
        return Array.from(locationMap.values());
      });
      
      const updatedLocations = await reorderSavedLocations(reorderedLocations);
      setSavedLocations(updatedLocations);
    } catch (error) {
      console.error('Error reordering locations:', error);
      setError('Failed to update route order. Please try again.');
    }
  }, []);

  const createLocationData = useCallback((): SavedLocationCreate => {
    // For updates, always use the exact locationName without fallback to coordinate-based name
    const locationNameToUse = selectedSavedLocationId 
      ? locationName 
      : (locationName || `Location ${location.latitude.toFixed(4)}, ${location.longitude.toFixed(4)}`);
    
    return {
      name: locationNameToUse,
      latitude: location.latitude,
      longitude: location.longitude,
      row_direction: parameters.row_direction,
      plant_height: parameters.plant_height,
      row_distance: parameters.row_distance,
      min_angle_from_row: parameters.min_angle_from_row,
      min_distance_from_row: parameters.min_distance_from_row,
      selected_date: selectedDate.toISOString().split('T')[0],
      time: formatTimeString(currentTime),
      optimal_time_windows: optimalWindows || []
    };
  }, [location, parameters, selectedDate, currentTime, optimalWindows, locationName, selectedSavedLocationId]);

  const handleSelectSavedLocation = useCallback((locationId: string, centerMap: boolean = false) => {
    const savedLocation = savedLocations.find(loc => loc.id === locationId);
    if (savedLocation) {
      handleSelectLocation(savedLocation);
      if (centerMap) {
        setLocation({
          latitude: savedLocation.latitude,
          longitude: savedLocation.longitude
        });
      }
    }
  }, [savedLocations, handleSelectLocation]);

  const handleToggleRoute = useCallback((visible: boolean) => {
    setIsRouteVisible(visible);
  }, []);

  const handleUpdateRouteGeometry = useCallback((geometry: any) => {
    setRouteGeometry(geometry);
  }, []);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <Header />
      {/* Main Content */}
      <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
        {/* Saved Locations Panel */}
        <Box
          sx={{
            width: 420,
            flexShrink: 0,
            height: 'calc(100% - 64px)',
            borderRight: '1px solid rgba(0, 0, 0, 0.12)',
            overflow: 'auto',
            '&::-webkit-scrollbar': {
              display: 'none'
            },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }}
        >
          <SavedLocationsPanel
            savedLocations={savedLocations}
            selectedLocationId={selectedSavedLocationId}
            onSelectLocation={(location) => {
              if (location.id === "-1") {
                // Special case for "Create New Location"
                handleSelectLocation(location);
              } else {
                // Use our new handler for saved locations
                handleSelectSavedLocation(location.id, true);
              }
            }}
            onDeleteLocation={handleDeleteLocation}
            loading={isLoadingSavedLocations}
            onImportLocations={handleImportLocations}
            onReorderLocations={handleReorderLocations}
            onToggleRoute={handleToggleRoute}
            onUpdateRouteGeometry={handleUpdateRouteGeometry}
            routeVisible={isRouteVisible}
          />
        </Box>
        
        {/* Main Content */}
        <Box 
          component="main" 
          sx={{ 
            flexGrow: 1, 
            p: 0,
            overflow: 'auto',
            '&::-webkit-scrollbar': {
              width: '8px'
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.2)',
              borderRadius: '4px'
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(0,0,0,0.05)'
            }
          }}
        >
          <Container maxWidth={false} disableGutters>
            <Grid container spacing={0} sx={{ height: { md: 'calc(100vh - 120px)' } }}>
              <Grid item xs={12} md={8} sx={{ height: { md: '100%' }, display: 'flex', flexDirection: 'column', pl: 1, pr: 1 }}>
                <Box sx={{ height: '52%', mb: 2 }}>
                  {isCreatingSession ? (
                    <Box sx={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', border: '1px dashed #ccc', borderRadius: 2 }}>
                      <CircularProgress size={40} />
                      <Typography variant="body1" sx={{ ml: 2 }}>
                        Loading map...
                      </Typography>
                    </Box>
                  ) : (
                    <MapboxMap 
                      latitude={location.latitude}
                      longitude={location.longitude}
                      zoom={15}
                      rowDirection={parameters.row_direction}
                      rowDistance={parameters.row_distance}
                      onLocationChange={handleLocationChange}
                      savedLocations={savedLocations}
                      selectedSavedLocationId={selectedSavedLocationId}
                      onSavedLocationSelect={(locationId) => handleSelectSavedLocation(locationId, false)}
                      routeVisible={isRouteVisible}
                      routeGeometry={routeGeometry}
                      onUpdateRouteGeometry={handleUpdateRouteGeometry}
                    />
                  )}
                </Box>
                <Box sx={{ height: 'calc(48% - 16px)', px: 2 }}>
                  <ShadowProjection 
                    shadowInfo={shadowInfo}
                    rowDirection={parameters.row_direction}
                    rowDistance={parameters.row_distance}
                    plantHeight={parameters.plant_height}
                    time={currentTime}
                    onTimeChange={handleTimeChange}
                    optimalTimeWindows={optimalWindows}
                    isSliderMoving={isSliderMoving}
                    onSaveClick={handleSaveClick}
                    isSavedLocation={selectedSavedLocationId !== null}
                    locationName={locationName}
                    onNameChange={handleLocationNameChange}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={4} sx={{ height: { md: '100%' }, pl: 1, pr: 1 }}>
                <ParameterPanel
                  parameters={parameters}
                  selectedDate={selectedDate}
                  currentTime={currentTime}
                  onParameterChange={handleParameterChange}
                  onDateChange={handleDateChange}
                  onNextDay={handleNextDay}
                  onPreviousDay={handlePreviousDay}
                  onTimeChange={handleTimeChange}
                  shadowInfo={shadowInfo}
                  optimalTimeWindows={optimalWindows}
                  isLoadingOptimal={isLoadingOptimal}
                  isSliderMoving={isSliderMoving}
                />
              </Grid>
            </Grid>
          </Container>
        </Box>
      </Box>
      
      {/* Save Location Dialog */}
      <SaveLocationDialog
        open={saveDialogOpen}
        onClose={() => setSaveDialogOpen(false)}
        onSave={handleSaveLocation}
        locationData={createLocationData()}
        defaultName={defaultLocationName}
        editingLocationId={selectedSavedLocationId}
      />
      
      {/* Error Snackbar */}
      <Snackbar
        open={error !== null}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setError(null)} 
          severity="error" 
          sx={{ 
            width: '100%',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
          }}
        >
          {error}
        </Alert>
      </Snackbar>
      
      {/* Success Snackbar */}
      <Snackbar
        open={successMessage !== null}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSuccessMessage(null)} 
          severity="success" 
          sx={{ 
            width: '100%',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
          }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MainApp; 